import { AppointmentStatus } from '@shared/types';
import { ROOM_ERROR_TYPES } from '@web-app/components/Daily/RoomErrorScreen/types';
import { addHours, isBefore } from 'date-fns';

type AppointmentBlockedErrorType =
  | typeof ROOM_ERROR_TYPES.APPOINTMENT_ENDED_PATIENT
  | typeof ROOM_ERROR_TYPES.APPOINTMENT_ENDED_PROVIDER
  | typeof ROOM_ERROR_TYPES.APPOINTMENT_CANCELLED
  | null;

/**
 * Checks if an appointment is blocked and returns the appropriate error type.
 *
 * Blocking rules:
 * - Cancelled, No-Show, Re-Scheduled → blocked after start + 2 hours (APPOINTMENT_CANCELLED)
 * - Occurred → blocked only if end time has passed (APPOINTMENT_ENDED_PATIENT/PROVIDER)
 * - null/undefined status → allowed
 *
 * @param appointment - Appointment data with status, start, and end time
 * @param isProvider - Whether the current user is a provider
 * @returns Error type to display, or null if access should be allowed
 */
export const getJoinBlockedReason = (
  appointment:
    | { status?: string | null; start?: string; end?: string }
    | undefined,
  isProvider: boolean,
): AppointmentBlockedErrorType => {
  if (!appointment) {
    return null;
  }

  const { status, start: startTime, end: endTime } = appointment;
  const now = new Date();

  // No status set - allow access
  if (!status) {
    return null;
  }

  // Cancelled, No-Show, Re-Scheduled → block after start + 2 hours
  if (
    status === AppointmentStatus.Cancelled ||
    status === AppointmentStatus.NoShow ||
    status === AppointmentStatus.Rescheduled
  ) {
    if (!startTime) {
      return null;
    }

    const blockAfter = addHours(new Date(startTime), 2);

    if (isBefore(blockAfter, now)) {
      return ROOM_ERROR_TYPES.APPOINTMENT_CANCELLED;
    }

    return null;
  }

  // Occurred → block only if end time has passed
  if (status === AppointmentStatus.Occurred) {
    if (!endTime) {
      return null;
    }

    const appointmentEnd = new Date(endTime);

    if (isBefore(appointmentEnd, now)) {
      return isProvider
        ? ROOM_ERROR_TYPES.APPOINTMENT_ENDED_PROVIDER
        : ROOM_ERROR_TYPES.APPOINTMENT_ENDED_PATIENT;
    }
  }

  return null;
};
